import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FolderOpen, Plus, Users } from "lucide-react";
import { Modal } from "../../../../shared/ui/modal";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onCreate: () => void;
    onManage: () => void;
    /** Absent on a patient or couple page: an open board has no record behind it. */
    onOpenBoard?: (() => void) | null;
    /** Named when the dock sits on a patient or couple page. */
    subjectLabel?: string | null;
};

/**
 * What the Genograma y Ecomapa dock entry opens.
 *
 * Deliberately the same shape as `SchedulerLaunchSheet` and
 * `PaymentsLaunchSheet`: a therapist who has learned one dock has learned the
 * others. It used to be a plain modal of full-width buttons with a close
 * cross, the one tool in the dock that opened something that looked like a
 * different app. It owns no data — the dock actions decide what each entry
 * does.
 */
export default function SystemicMapLaunchSheet({
    isOpen,
    onClose,
    onCreate,
    onManage,
    onOpenBoard = null,
    subjectLabel = null,
}: Props) {
    const { t } = useTranslation();

    const entry = (
        onClick: () => void,
        icon: React.ReactNode,
        title: string,
        hint: string,
        primary = false,
    ) => (
        <button
            type="button"
            onClick={onClick}
            className={`mb-4 flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition-colors ${
                primary
                    ? "border-brand-100 bg-brand-50 text-brand-700 hover:bg-brand-100 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300"
                    : "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800/50"
            }`}
        >
            <span
                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                    primary
                        ? "bg-white text-brand-600 shadow-sm dark:bg-gray-800"
                        : "bg-gray-100 text-gray-500 dark:bg-gray-800"
                }`}
            >
                {icon}
            </span>
            <span className="flex-1">
                <span className="block text-sm font-semibold">{title}</span>
                <span className="block text-xs opacity-80">{hint}</span>
            </span>
        </button>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton={false}
            ariaLabel={t("systemicMap.toolName")}
            className="mx-4 max-w-lg rounded-3xl"
        >
            <motion.div
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="flex max-h-[80vh] flex-col p-5"
            >
                <div className="mb-4">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                        {t("systemicMap.toolName")}
                    </h2>
                    <p className="text-sm text-content-tertiary">
                        {subjectLabel
                            ? t("systemicMap.dock.forPatient", { patient: subjectLabel })
                            : t("systemicMap.dock.subtitle")}
                    </p>
                </div>

                {entry(
                    onCreate,
                    <Plus className="h-5 w-5" />,
                    t("systemicMap.dock.create"),
                    t("systemicMap.dock.createHint"),
                    true,
                )}

                {onOpenBoard &&
                    entry(
                        onOpenBoard,
                        <Users className="h-5 w-5" />,
                        t("systemicMap.dock.openBoard"),
                        t("systemicMap.dock.openBoardHint"),
                    )}

                {entry(
                    onManage,
                    <FolderOpen className="h-5 w-5" />,
                    t("systemicMap.dock.manage"),
                    t("systemicMap.dock.manageHint"),
                )}

                <button
                    type="button"
                    onClick={onClose}
                    className="mt-2 w-full rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800/50"
                >
                    {t("common.cancel")}
                </button>
            </motion.div>
        </Modal>
    );
}
